<script setup lang="ts">
  import { storeToRefs } from "pinia";
  import { newDateTalk, Talk } from "~/entities/Talk";
  import type { TalkRoomUser } from "~/entities/TalkRoomUser";
  import { useLoginStore } from "~/stores/LoginStore";
  import { usePlanStore } from "~/stores/PlanStore";
  import { useTalkRoomStore } from "~/stores/TalkRoomStore";
  import { useSnackBarStore } from "~/stores/SnackBarStore";
  import TalkFileInput from "~/components/Organisms/TalkFileInput.vue";
  import TalkInputMessage from "~/components/Organisms/TalkInputMessage.vue";
  import TalkMessage from "~/components/Organisms/TalkMessage.vue";

  interface Props {
    open: boolean;
    planId: string;
    talkRoomId: string;
    threadParentTalkId: string;
    targetTalkId?: string;
    canAction: boolean; // アクション可能かどうか
    canLike: boolean; // いいね可能かどうか
    isOwner: boolean; // 自分が講師かどうか
    isOwnerOrMember: boolean; // 自分が講師・入会者かどうか
    ownerIds: string[];
    userGoodList: string[];
    isTest?: boolean; // テスト時のみ true にする
  }
  const props = defineProps<Props>();

  interface Emits {
    (event: "update:open", open: boolean): void;
    (event: "send"): void;
    (event: "onLikeClick", talk: Talk): void;
    (event: "onUserClick", talk: TalkRoomUser): void;
  }
  const emit = defineEmits<Emits>();

  const isOpen = computed<boolean>({
    get: () => props.open,
    set: value => {
      emit("update:open", value);
    },
  });

  const loginStore = useLoginStore();
  const { loginInfo } = storeToRefs(loginStore);

  const planStore = usePlanStore();
  const { plan } = storeToRefs(planStore);

  const talkRoomStore = useTalkRoomStore();
  const { talks, threadTalks, updateThreadTalk } = storeToRefs(talkRoomStore);
  const threadParentTalk = computed(() => {
    // store に必ず読み込んでいる前提で、親トークを取得
    return talks.value.find(t => t.talkId === props.threadParentTalkId)!;
  });
  const threadParentTalkDate = computed(() => {
    if (!threadParentTalk.value?.postDateTime) {
      return undefined;
    }
    return newDateTalk(threadParentTalk.value!.postDateTime!);
  });

  watch(threadParentTalk, () => {
    if (!threadParentTalk.value.canView()) {
      // 親トークが表示不可となったらモーダルを閉じる
      isOpen.value = false;
    }
  });

  const activeThreadTalks = computed<Talk[]>(() => {
    const talksWithDates = insertDatesToTalks(
      threadTalks.value.filter(t => t.canView()),
      true,
      props.isOwnerOrMember,
    );
    return talksWithDates.filter(t => {
      if (t.messageType === "date" && threadParentTalkDate.value) {
        // 日付の場合、親の日付と同じ場合は表示しない
        return threadParentTalkDate.value.date !== t.date;
      }
      // 日付以外はすべてOK
      return true;
    });
  });
  const talkRoomName = computed(() => {
    const talkRoom = talkRoomStore.talkRooms.find(tr => tr.talkRoomId === props.talkRoomId);
    return omitString(getTalkRoomLabel(talkRoom!, plan.value!), 20);
  });

  // 画像メッセージリスト。前後移動のために保持しておく
  const filteredImageTalks = computed<Talk[]>(() => {
    if (!threadParentTalk.value) {
      return [];
    }
    // スレッド内親子トークのうち画像メッセージのみに絞り込む
    const parentImageTalk = threadParentTalk.value.messageType === "image" ? [threadParentTalk.value] : [];
    const childImageTalks = activeThreadTalks.value.filter(t => {
      return t.messageType === "image";
    });
    return parentImageTalk.concat(childImageTalks);
  });

  const snackBarStore = useSnackBarStore();

  const talkMessageRefs = ref<InstanceType<typeof TalkMessage>[]>([]);
  const talkInputMessage = ref<InstanceType<typeof TalkInputMessage> | null>(null);
  const isSending = ref(false);
  const showInRoom = ref(false);

  const sendFunc = async (message: string, mentionUserIds: string[]) => {
    await talkRoomStore.sendTalk(
      props.talkRoomId,
      message.trim(),
      mentionUserIds,
      undefined,
      props.threadParentTalkId,
      showInRoom.value,
    );
    emit("send");
  };
  const onSend = () => {
    talkInputMessage.value?.triggerSend();
  };
  const onSent = () => {
    showInRoom.value = false;
  };
  const onFailed = (error: any) => {
    sentryErrorLog(`トークスレッドメッセージ送信失敗 ${error}`);
    snackBarStore.setError("送信に失敗しました");
  };

  // ファイル入力コンポーネント
  const fileInput = ref<InstanceType<typeof TalkFileInput> | null>(null);

  // ファイルドラッグ＆ドロップ、ペースト時の処理
  const onFiles = (files: FileList) => {
    fileInput.value?.validateSendFiles(files);
  };

  // スクロール処理
  // - targetTalkId が指定されている場合はそのトークまでスクロール（アニメあり）
  // - デフォルトおよびトークが増えた場合は最下部へスクロール（アニメなし）
  // deep: true とすることで配列の中身が変わった場合も監視する
  // ただし deep 設定すると new/old の値比較ができなくなるため、配列長変化をチェックするために前回の配列をシャローコピーしておく
  let prevThreadTalks: Talk[] = [];
  let isFirstScroll = true;
  watch(
    threadTalks,
    () => {
      if (prevThreadTalks.length < threadTalks.value.length) {
        if (loginStore.isLogin()) {
          // ログイン中にトークが増えた場合は末尾を既読処理
          const lastTalk = threadTalks.value.slice(-1)[0];
          talkRoomStore.setThreadLastRead(
            loginInfo.value.userId,
            props.talkRoomId,
            lastTalk.threadTalkId,
            lastTalk.talkId,
          );
        }

        // スクロール処理
        // 初回かつ targetTalkId が指定されている場合はアニメーション付き
        if (isFirstScroll && props.targetTalkId) {
          scrollToTalk(props.targetTalkId, true);
        } else {
          scrollToTalk(threadTalks.value.slice(-1)[0].talkId, false);
        }
        isFirstScroll = false;
      }
      prevThreadTalks = [...threadTalks.value];
    },
    { deep: true },
  );

  // OGP・ブックマークなど非同期取得結果を検知
  watch(updateThreadTalk, () => {
    if (updateThreadTalk.value) {
      talkRoomStore.clearUpdateThreadTalkFlag();
      threadTalks.value = [...threadTalks.value]; // コピーにより再描画を促す
    }
  });

  const getTalkElement = (talkId: string) => {
    const targetTalks = document.querySelectorAll(`[data-thread-talkid="${talkId}"]`);
    if (targetTalks.length === 0) {
      return null;
    }
    return targetTalks[0];
  };

  // 特定のトークまでスクロール
  const scrollToTalk = (talkId: string, animate: boolean) => {
    // スクロールするには DOM が更新されてからでないといけないので、複数回待ち受ける
    let count = 0;
    const tm = setInterval(() => {
      consoleLog("[FINCS-3827] TalkMessageThread.scrollToTalk", talkId, animate, count);
      if (scrollToTalkInDom(talkId, animate)) {
        // スクロールできれば終了
        clearInterval(tm);
      } else if (count < 10) {
        // スクロールできなければリトライ
        count++;
      } else {
        // 10回までリトライしてもスクロールできなければ終了
        clearInterval(tm);
        snackBarStore.setError("スレッド内にメッセージが見つかりません");
      }
    }, 50);
  };

  // DOM 内の特定のトークまでスクロール
  const scrollToTalkInDom = (talkId: string, animate: boolean) => {
    const targetTalk = getTalkElement(talkId);
    if (!targetTalk) {
      return false;
    }
    requestAnimationFrame(() => {
      targetTalk.scrollIntoView({ behavior: "instant" });
      if (animate && talkMessageRefs.value?.find) {
        const talkMessage = talkMessageRefs.value.find((tm: InstanceType<typeof TalkMessage>) => tm.talkId === talkId);
        if (talkMessage) {
          // スレッド内トークメッセージのアニメーション
          (talkMessage as InstanceType<typeof TalkMessage>).animateFound();
        }
      }
    });
    return true;
  };

  onMounted(() => {
    // トークスレッド取得開始
    threadTalks.value = [];
    talkRoomStore.startThreadTalksObserve(props.planId, props.talkRoomId, props.threadParentTalkId);
  });
  onUnmounted(() => {
    // トークスレッド取得終了
    talkRoomStore.stopThreadTalksObserve();
  });

  // レスポンシブ対応
  const isSp = isSpDisplayRef();
</script>

<template>
  <v-dialog v-model="isOpen" scrim="black" :attach="!!isTest" transition="slide-x-reverse-transition">
    <v-sheet class="dialog py-4 rounded" :class="isSp ? 'px-4' : 'px-10'" color="monochrome-04">
      <v-icon icon="$mdiChevronLeft" class="back-button" @click="isOpen = false" />

      <!-- ファイルドラッグ＆ドロップ、ペーストを処理 -->
      <AtomsFileDrop :can-action="canAction" @files="onFiles">
        <div class="d-flex flex-column">
          <div class="mb-2 ml-10">
            <div>トークスレッド</div>
            <div class="my-1 d-flex">
              <div class="text-monochrome-02" style="font-size: 14px">{{ talkRoomName }}</div>
            </div>
          </div>
          <div style="width: 100%; max-height: calc(100svh - 200px); overflow-y: auto">
            <!-- 親トーク日付 -->
            <AtomsTalkDate v-if="threadParentTalkDate" :label="threadParentTalkDate.date" />
            <!-- 親トークメッセージ -->
            <!-- TODO scroll, search-word パラメータは仮 -->
            <OrganismsTalkMessage
              :key="threadParentTalkId"
              :plan-id="planId"
              :talk-room-id="talkRoomId"
              :talk="threadParentTalk"
              :can-action="canAction"
              :can-like="canLike"
              :is-owner="isOwner"
              :is-owner-or-member="isOwnerOrMember"
              :image-talks="filteredImageTalks"
              :owner-ids="ownerIds"
              :user-good-list="userGoodList"
              :scroll="() => {}"
              :search-word="``"
              :is-thread-view="true"
              :data-thread-talkid="threadParentTalkId"
              @on-user-click="u => emit('onUserClick', u)"
              @on-like-click="t => emit('onLikeClick', t)"
            />

            <!-- 区切り線 -->
            <div class="my-4" style="width: 100%; height: 2px; background-color: #e0e0e0"></div>

            <!-- スレッド内トークメッセージ一覧 -->
            <div class="mt-4 mb-2">
              <div v-for="threadTalk in activeThreadTalks" :key="threadTalk.talkId" class="d-flex flex-column">
                <div class="align-center">
                  <!-- スレッド内トーク日付 -->
                  <AtomsTalkDate v-if="threadTalk.messageType === 'date'" :label="threadTalk.date!" />
                  <!-- スレッド内トークメッセージ -->
                  <!-- TODO scroll, search-word パラメータは仮 -->
                  <OrganismsTalkMessage
                    v-else
                    ref="talkMessageRefs"
                    :key="threadTalk.talkId"
                    :plan-id="planId"
                    :talk-room-id="talkRoomId"
                    :talk="threadTalk"
                    :can-action="canAction"
                    :can-like="canLike"
                    :is-owner="isOwner"
                    :is-owner-or-member="isOwnerOrMember"
                    :image-talks="filteredImageTalks"
                    :owner-ids="ownerIds"
                    :user-good-list="userGoodList"
                    :scroll="() => {}"
                    :search-word="``"
                    :is-thread-view="true"
                    :data-thread-talkid="threadTalk.talkId"
                    @on-user-click="u => emit('onUserClick', u)"
                    @on-like-click="t => emit('onLikeClick', t)"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- トークスレッド入力 -->
          <div class="talk-thread-input">
            <!-- チャンネルにも表示 -->
            <AtomsCheckBox
              v-model:is-checked="showInRoom"
              class="talk-thread-input-show-in-room ml-2 mb-6"
              label="チャンネルにも表示"
            ></AtomsCheckBox>

            <div class="d-flex align-center">
              <div v-if="!canAction" class="can_not_action">このトークには投稿できません</div>

              <!-- ファイル添付 -->
              <OrganismsTalkFileInput
                ref="fileInput"
                :disabled="isSending"
                :talk-room-id="talkRoomId"
                :thread-talk-id="threadParentTalkId"
                :show-in-room="showInRoom"
                @send="onSent"
              />

              <!-- テキスト入力 -->
              <!-- 保存先ルームIDは、スレッドユニークにするためにトークIDも付与 -->
              <OrganismsTalkInputMessage
                ref="talkInputMessage"
                v-model:is-sending="isSending"
                class="talk-thread-input-text"
                style="flex-grow: 1"
                :talk-room-id-to-save="`${talkRoomId}_${threadParentTalkId}`"
                :allow-empty-send="false"
                placeholder="入力..."
                :fixed-position="false"
                :with-send-button-margin="true"
                :send-func="sendFunc"
                @sent="onSent"
                @failed="onFailed"
              />
              <!-- 送信 -->
              <div class="talk-thread-input-send d-flex align-center">
                <v-sheet class="chat-send-button" color="transparent" data-testid="send-talk-message" @click="onSend">
                  <AtomsSvgIcon :width="18" :height="18" :img-path="`assets/images/icon/chat-message-send.svg`" />
                </v-sheet>
              </div>
            </div>
          </div>
        </div>
      </AtomsFileDrop>
    </v-sheet>
  </v-dialog>
</template>

<style lang="scss" scoped>
  @import "assets/media.scss";

  .dialog {
    width: 100svw;
    height: 100svh;
    max-width: 1024px;
    display: block;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    -webkit-transform: translate(-50%, -50%);
    -ms-transform: translate(-50%, -50%);
    overflow-y: auto;
  }
  .back-button {
    position: absolute;
    top: 22px;
    left: 20px;
    color: #8a8a8a;
    cursor: pointer;
  }
  .talk-thread-input {
    width: 100%;
    position: absolute;
    bottom: 0;
    left: 0;
    background-color: rgba(50, 50, 50, 1);
    padding: 10px;
    border-radius: 8px;
  }
  .talk-thread-input-send {
    position: absolute;
    right: 24px;
    height: 40px !important;
    cursor: pointer;
  }
  .can_not_action {
    position: absolute;
    z-index: 10;
    text-align: center;
    width: 100%;
    background-color: #000000ad;
    height: 100%;
    padding-top: 72px;
    top: 0;
    left: 0;
    @include display_sp {
      font-size: 14px;
    }
  }
</style>
